import { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { BeforeInstallPromptEvent, setupInstallPrompt, isAppInstalled } from '@/lib/pwa';

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (isAppInstalled()) {
      setIsInstalled(true);
      return;
    }

    // Dismissed before?
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const now = new Date();
      const daysSinceDismissed = (now.getTime() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      
      // Show again after 7 days
      if (daysSinceDismissed < 7) return;
    }

    // Listen for install prompt
    setupInstallPrompt((e) => {
      setDeferredPrompt(e);
      
      // Show prompt after 5 seconds
      setTimeout(() => setShowPrompt(true), 5000);
    });

    // Check if installed
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowPrompt(false);
    });
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();

    const choiceResult = await deferredPrompt.userChoice;

    if (choiceResult.outcome === 'accepted') {
      setShowPrompt(false);
    } else {
    }

    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', new Date().toISOString());
  };

  if (isInstalled || !showPrompt) return null;

  return (
    <Card 
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 p-4 shadow-lg z-50 border-2 border-primary/20 bg-white dark:bg-gray-900"
      data-testid="card-install-prompt"
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
          <Smartphone className="w-6 h-6 text-primary" />
        </div>
        
        <div className="flex-1">
          <h3 className="font-semibold text-base mb-1" data-testid="text-install-title">
            Ana Ekrana Ekle
          </h3>
          <p className="text-sm text-muted-foreground mb-3" data-testid="text-install-description">
            TemizFilo'yu telefonunuzun ana ekranına ekleyerek hızlı erişim sağlayın
          </p>
          
          <div className="flex gap-2">
            <Button 
              onClick={handleInstall}
              size="sm"
              className="flex-1"
              data-testid="button-install-app"
            >
              <Download className="w-4 h-4 mr-2" />
              Yükle
            </Button>
            <Button 
              onClick={handleDismiss}
              variant="ghost"
              size="sm"
              data-testid="button-dismiss-install"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
